import {
  CdcmResponse,
  RecordsSearchFilterResponse,
  JsonSchema,
  IOption,
  UserData,
  ISelectableOption,
  ToastInfo,
  IAMUserState,
  LandingPageDropdownErrors,
  SetupPermission,
  GeneralPermissions,
  LandingPageOptions,
  LaunchMode,
} from "../../../types";
import { messages } from "../messages";
import { getIntl } from "../../../translate/i18n";
import { EffectiveUserContext } from "../../../contexts/user/launch/resolveLaunchContext";

export const DEFAULT_LANDING_PAGE_ERRORS: LandingPageDropdownErrors = {
  department: {
    error: false,
  },
  store: {
    error: false,
  },
  setup: {
    error: false,
  },
};

export const DEFAULT_OPTION: IOption = {
  label: "",
  value: "",
  disabled: true,
};

export const NO_RESULTS_DEFAULT_IOPTION = (): IOption => ({
  label: getIntl().formatMessage(messages.results),
  value: "",
  disabled: true,
});

export const LOADING_DEFAULT_IOPTION = (): IOption => ({
  label: getIntl().formatMessage(messages.loading),
  value: "",
  disabled: true,
});

export const DEFAULT_ISelectableOption: ISelectableOption = {
  label: "",
  value: "",
  disabled: true,
};

export const NO_RESULTS_DEFAULT_ISelectableOption = (): ISelectableOption => ({
  label: getIntl().formatMessage(messages.results),
  value: "",
  disabled: true,
});

export const LOADING_DEFAULT_ISelectableOption = (): ISelectableOption => ({
  label: getIntl().formatMessage(messages.loading),
  value: "",
  disabled: true,
});

export const DEFAULT_RecordsSearchFilterResponse: RecordsSearchFilterResponse =
  {
    message: "",
    data: [],
  };

export const DEFAULT_USER_EDIT_PERMISSIONS: GeneralPermissions = {
  hasAuditAccess: false,
};

export const SETUP_PERMISSIONS_NONE: SetupPermission = {
  hasView: false,
  hasManage: false,
  hasImport: false,
};

export const SETUP_PERMISSIONS_ALL: SetupPermission = {
  hasView: true,
  hasManage: true,
  hasImport: true,
};

export const DEFAULT_DEPARTMENT_OPTIONS: IOption[] = [
  {
    label: "Accounting",
    value: "accounting",
    disabled: false,
  },
  {
    label: "Parts",
    value: "parts",
    disabled: false,
  },
  {
    label: "Sales",
    value: "sales",
    disabled: false,
  },
  {
    label: "Service",
    value: "service",
    disabled: false,
  },
  {
    label: "General",
    value: "general",
    disabled: false,
  },
];

export const DEFAULT_LANDING_PAGE_OPTIONS: LandingPageOptions = {
  store: DEFAULT_OPTION,
  department: DEFAULT_OPTION,
  setup: DEFAULT_OPTION,
  departmentId: DEFAULT_OPTION,
  groupId: DEFAULT_OPTION,
};

export const DEFAULT_SCHEMA = (): JsonSchema => ({
  $schema: "http://json-schema.org/draft-07/schema#",
  $id: "http://cdk.com/schemas/cdcm-setup-schema.json",
  title: getIntl().formatMessage(messages.title),
  description: getIntl().formatMessage(messages.desc),
  functional_area: "Example",
  type: "object",
  properties: {},
  required: [],
  sections: {},
});

export const DEFAULT_USER_DATA: UserData = {
  user: {
    userGuid: "",
    loginId: "",
    firstName: "",
    middleInitial: "",
    lastName: "",
    dmsUserIdentifier: "",
    locked: false,
    adminNotes: "",
    creationDate: "",
    lastLoginDate: "",
    locale: "",
    state: 0,
    prefix: "",
    suffix: "",
    nickName: "",
    employeeId: "",
    jobTitle: "",
    selfServiceEnabled: 0,
    accountStatus: "",
    communicationEdgeId: "",
    uniquePrincipalName: "",
    createdBy: "",
    mfaEnabled: false,
    externallyFederated: false,
    dmsUserStatus: null,
  },
  contacts: [],
  enterprise: {
    idpName: "",
    id: "",
    name: "",
    vanityDomain: "",
  },
  stores: [],
  roles: [],
  alternateIdentifiers: [],
  roleAssignedBy: "",
  roleAssignedOn: 0,
  warnings: [],
  securityQuestionsStatus: false,
  userAliases: [],
};

export const DEFAULT_IAM_USER_DATA: IAMUserState = {
  iamUser: {
    username: "",
    authorities: [],
    accountNonExpired: true,
    accountNonLocked: true,
    credentialsNonExpired: true,
    enabled: true,
    enterprise: {
      id: "",
    },
    assignments: [],
    firstName: "Office",
    middleName: null,
    lastName: "Manager",
    locale: "en_US",
    isServiceUser: false,
  },
  extendedAccountInfo: [],
  eisGroups: { groups: [] },
};

export const DEFAULT_RECORD_RESPONSE = (): CdcmResponse => ({
  resultCode: "CDK_400",
  resultDescription: "KO",
  resultObj: getIntl().formatMessage(messages.response),
  executionTime: 0,
});

export const DEFAULT_SEARCH_RESPONSE: CdcmResponse = {
  resultCode: "CDK_400",
  resultDescription: "KO",
  resultObj: {
    message: "",
    data: [],
  },
  executionTime: 0,
};

export const DEFAULT_DEPARTMENTS_RESPONSE: CdcmResponse = {
  resultCode: "CDK_200",
  resultDescription: "KO",
  resultObj: {},
  executionTime: 0,
};

export const DEFAULT_SETUPS_RESPONSE: CdcmResponse = {
  resultCode: "CDK_200",
  resultDescription: "KO",
  resultObj: [
    {
      department: "",
      setups: [],
    },
  ],
  executionTime: 0,
};

export const DEFAULT_FAVORITE_SETUPS_RESPONSE: CdcmResponse = {
  resultCode: "CDK_500",
  resultDescription: "KO",
  resultObj: [],
  executionTime: 0,
};

export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "15", value: 15 },
  { label: "25", value: 25 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
];

export const DEFAULT_TOAST: ToastInfo = {
  message: "",
  variant: "neutral",
};

export const TOAST_MESSAGES = {
  SAVING: () =>
    ({
      variant: "neutral",
      message: getIntl().formatMessage(messages.saving),
    }) as ToastInfo,
  SAVING_WITH_TITLE: (title: string) =>
    ({
      variant: "neutral",
      message: getIntl().formatMessage(messages.savingWithTitle, { title }),
    }) as ToastInfo,
  DELETING: () =>
    ({
      variant: "neutral",
      message: getIntl().formatMessage(messages.delete),
    }) as ToastInfo,
  IMPORTING: () =>
    ({
      variant: "neutral",
      message: getIntl().formatMessage(messages.import),
    }) as ToastInfo,
  VALIDATION_ERRORS: (fields: string) =>
    ({
      variant: "warning",
      message: `Please fix validation errors in: ${fields}`,
    }) as ToastInfo,
  COPYING: {
    variant: "neutral",
    message: "Copying...",
  },
  NO_DATA_TO_SAVE: () =>
    ({
      variant: "warning",
      message: getIntl().formatMessage(messages.noDataToSave),
    }) as ToastInfo,
  // eslint-disable-next-line
  UN_IMPLEMENTED_SETUP: (setupName: string) =>
    ({
      variant: "negative",

      message: getIntl().formatMessage(messages.unimplementedSetup, {
        setupName,
      }),
    }) as ToastInfo,
  USER_DATA_FAILURE: () =>
    ({
      variant: "negative",
      message: getIntl().formatMessage(messages.userDataFailure),
    }) as ToastInfo,
  IAM_USER_DATA_FAILURE: () =>
    ({
      variant: "warning",
      message: getIntl().formatMessage(messages.iamUserDataFailure),
    }) as ToastInfo,
  PO_GROUPS_RECORD_REQUIRED: () =>
    ({
      variant: "warning",
      message: getIntl().formatMessage(messages.poGroupsRecordRequired),
    }) as ToastInfo,
  UANBLE_TO_FETCH_RECORD: () =>
    ({
      variant: "negative",
      message: getIntl().formatMessage(messages.unableToFetchRecord),
    }) as ToastInfo,
  FILE_UPLOAD_TEMPLATE_DOWNLOADED: () =>
    ({
      variant: "neutral",
      message: getIntl().formatMessage(messages.fileUploadTemplateDownloaded),
    }) as ToastInfo,
  FILE_UPLOAD_TEMPLATE_DOWNLOAD_FAILED: () =>
    ({
      variant: "negative",

      message: getIntl().formatMessage(
        messages.fileUploadTemplateDownloadFailed,
      ),
    }) as ToastInfo,
  NO_FILE_SELECTED: () =>
    ({
      variant: "negative",

      message: getIntl().formatMessage(messages.noFileSelected, {
        defaultMessage: "No file selected",
      }),
    }) as ToastInfo,
  EMPTY_FILE: () =>
    ({
      variant: "negative",

      message: getIntl().formatMessage(messages.emptyFile, {
        defaultMessage: "Empty file",
      }),
    }) as ToastInfo,
  FILE_PARSE_ERROR: () =>
    ({
      variant: "negative",

      message: getIntl().formatMessage(messages.fileParseError, {
        defaultMessage: "Unable to parse file. Please retry.",
      }),
    }) as ToastInfo,
  INVALID_FILE_TYPE: () =>
    ({
      variant: "negative" as const,

      message: getIntl().formatMessage(messages.fileUploadInvalidType, {
        defaultMessage: "Invalid file type. Please upload XLS, XLSX, or CSV.",
      }),
    }) as ToastInfo,
  FILE_TOO_LARGE: () =>
    ({
      variant: "negative" as const,

      message: getIntl().formatMessage(messages.fileUploadFileTooLarge, {
        defaultMessage: "File size exceeds 5 MB",
      }),
    }) as ToastInfo,
  FILE_VALIDATION_ERROR: () =>
    ({
      variant: "negative",
      message: getIntl().formatMessage(messages.fileValidationError),
    }) as ToastInfo,
  FILE_IMPORT_SUCCESS_STATUS: (successCount: number) =>
    ({
      variant: "positive",
      message: `Successfully imported ${successCount} records.`,
    }) as ToastInfo,
  FILE_IMPORT_STATUS: (
    successCount: number,
    failedCount: number,
    abortedCount: number,
  ) =>
    ({
      variant: "negative",
      message: `Import status: ${successCount} imported, ${failedCount} failed, ${abortedCount} aborted`,
    }) as ToastInfo,
  AR_CONVERSION_IN_PROGRESS: () =>
    ({
      variant: "neutral",
      message: getIntl().formatMessage(messages.conversion),
    }) as ToastInfo,
  SCHEMA_ERROR: (setupName: string) =>
    ({
      variant: "negative",
      message: `Unable to read schema for ${setupName}. Please retry.`,
    }) as ToastInfo,
  INVENTORY_BUILDING_PRIORITIES_UPDATED: () =>
    ({
      variant: "positive",
      message: "Inventory building priorities updated successfully.",
    }) as ToastInfo,
  INVENTORY_BUILDING_PRIORITIES_UPDATE_FAILED: () =>
    ({
      variant: "negative",
      message: "Unable to update inventory building priorities. Please retry.",
    }) as ToastInfo,
  INVENTORY_BUILDING_PRIORITIES_INVALID_SEQUENCE: () =>
    ({
      variant: "warning",

      message:
        "Building priorities are not in the expected sequence. Reorder is not possible right now. Please retry.",
    }) as ToastInfo,
  MISSING_IMPORT_COLUMNS: (missingHeaders: string) =>
    ({
      variant: "negative",
      message: `The file you want to upload does not match the required template. Missing columns: ${missingHeaders}. Please retry.`,
    }) as ToastInfo,
  SHEET_NAME_MISMATCH: (setupName: string) =>
    ({
      variant: "negative",
      message: `Invalid worksheet name. Please upload a file with the sheet name '${setupName}'.`,
    }) as ToastInfo,
  FILE_PROCESSING_ERROR: () =>
    ({
      variant: "negative",
      message: "Errors found while processing the file.",
    }) as ToastInfo,
  FILE_VALIDATION_SUCCESS: () =>
    ({
      variant: "positive",

      message:
        "File validation completed successfully. Click Proceed to import the records.",
    }) as ToastInfo,
  UNSAVED_CHANGES_WARNING: () =>
    ({
      variant: "warning",

      message:
        "You have unsaved changes. Please save or discard your changes before continuing.",
    }) as ToastInfo,
  OVERRIDE_WARNING: () =>
    ({
      variant: "warning",
      message: [
        "File validation completed successfully. Click Proceed to import the records",
        "Note: This action will overwrite existing data.",
      ],
    }) as ToastInfo,
} as const;

export const DROP_DOWN_OPTIONS = {
  MIN_CHARACTERS_FOR_SEARCH: (minChars: number) => {
    return {
      label: `Please enter at least ${minChars} characters to search.`,
      value: "",
      disabled: true,
    };
  },
};

export const CDCM_IFRAME_LAUNCH_MODES: LaunchMode[] = ["payments", "unify"];

export const IFRAME_KEYS = {
  MODE: {
    PAYMENTS_MODE: "payments",
    UNIFY_MODE: "unify",
  },
  PARAMS: {
    ENTERPRISE_ID: "enterpriseid",
    STORE_ID: "storeid",
    LAUNCH_MODE: "launchmode",
  },
};

export const RESOLVED_INITIAL_LAUNCH_CONTEXT: EffectiveUserContext = {
  launchMode: "default",
  effectiveEnterpriseId: "",
  effectiveStoreOption: DEFAULT_OPTION,
  effectiveIamUserState: DEFAULT_IAM_USER_DATA,
  effectiveIamUserStoresCommaSeparated: "",
};

export const AUTHORIZED_INTERNAL_USER_ENTERPRISE_ID = "E000000";
